import * as THREE from 'three';
import { MISSIONS, Mission } from './MissionDefinitions';
import { VehiclePhysics } from '../vehicle/VehiclePhysics';
import { StVOExaminer } from '../stvo/StVOExaminer';
import { TownBuilder } from '../environment/TownBuilder';

export interface MissionResult {
  missionId: string;
  missionTitle: string;
  passed: boolean;
  score: number;
  durationSeconds: number;
  infractionsCount: number;
  fatalCount: number;
  feedback: string;
}

export interface NavInstruction {
  id: string;
  icon: string; // '⬅️' | '➡️' | '⬆️' | '🔄' | '🅿️' | '⚡' | '🛑' | '🧭'
  title?: string;
  text: string;
  spokenText?: string;
  audioKey?: string;
  subText?: string;
  distanceMeters?: number;
}

export class MissionManager {
  public currentMission: Mission = MISSIONS[0];
  public elapsedTime: number = 0;
  public isCompleted: boolean = false;
  public result: MissionResult | null = null;

  // Turn-by-turn Fahrprüfer navigation instruction
  public currentNavInstruction: NavInstruction | null = null;
  public activeInstructionId: string = '';

  // Specific mission state trackers
  private emergencyBrakingTriggered: boolean = false;
  private emergencyBrakingReactionStart: number = 0;
  private emergencyBrakingBrakeTime: number = 0;
  private emergencyBrakingCompleted: boolean = false;

  private roundaboutEntered: boolean = false;
  private roundaboutCirculateTime: number = 0;

  private wendenReverseCount: number = 0;
  private wendenWasReversing: boolean = false;
  private wendenInitialForwardZ: number = -1;

  constructor(private examiner: StVOExaminer, private town: TownBuilder) {}

  public setNavInstruction(instruction: NavInstruction, speak: boolean = true) {
    if (this.activeInstructionId !== instruction.id) {
      this.activeInstructionId = instruction.id;
      this.currentNavInstruction = { ...instruction };
      if (speak) {
        this.say(instruction.spokenText || instruction.text, instruction.audioKey || instruction.id);
      }
    } else if (this.currentNavInstruction) {
      this.currentNavInstruction.distanceMeters = instruction.distanceMeters;
      if (instruction.subText) this.currentNavInstruction.subText = instruction.subText;
      if (instruction.title) this.currentNavInstruction.title = instruction.title;
      if (instruction.text) this.currentNavInstruction.text = instruction.text;
      if (instruction.icon) this.currentNavInstruction.icon = instruction.icon;
      if (instruction.spokenText) this.currentNavInstruction.spokenText = instruction.spokenText;
      if (instruction.audioKey) this.currentNavInstruction.audioKey = instruction.audioKey;
    }
  }

  public startMission(missionId: string, vehiclePhysics: VehiclePhysics) {
    const found = MISSIONS.find((m) => m.id === missionId);
    if (!found) return;

    this.currentMission = found;
    this.elapsedTime = 0;
    this.isCompleted = false;
    this.result = null;
    this.emergencyBrakingTriggered = false;
    this.emergencyBrakingReactionStart = 0;
    this.emergencyBrakingBrakeTime = 0;
    this.emergencyBrakingCompleted = false;

    this.roundaboutEntered = false;
    this.roundaboutCirculateTime = 0;

    this.wendenReverseCount = 0;
    this.wendenWasReversing = false;
    this.wendenInitialForwardZ = Math.cos(found.startRotationY);

    this.examiner.infractionLog.reset();
    vehiclePhysics.resetPosition(
      found.startPosition as unknown as import('cannon-es').Vec3,
      found.startRotationY
    );

    // Reset free-drive navigation state for fresh start
    this.navStepIndex = 0;
    this.lastNavInstructionTime = 0;
    this.hasDeparted = false;
    this.navAnnouncedZones = new Set<number>();
    this.activeInstructionId = '';
    this.currentNavInstruction = null;

    // Set initial mission guidance
    if (this.currentMission.id === 'laengsparken') {
      this.setNavInstruction({
        id: 'laengsparken_start',
        icon: '🅿️',
        title: 'LÄNGSPARKEN',
        text: 'Rückwärts in die markierte Parklücke einparken.',
        subText: 'Blinker rechts • 3-S-Blick • Max. 2 Korrekturzüge',
      }, false);
    } else if (this.currentMission.id === 'querparken') {
      this.setNavInstruction({
        id: 'querparken_start',
        icon: '🅿️',
        title: 'QUERPARKEN',
        text: 'Rückwärts mittig in die grün markierte 90°-Parkbucht einparken.',
        subText: 'Blinker rechts • 3-S-Blick • Rückraum sichern',
      }, false);
    } else if (this.currentMission.id === 'umkehren_wenden') {
      this.setNavInstruction({
        id: 'wenden_start',
        icon: '🔄',
        title: 'UMKEHREN (3-PUNKT-WENDEN)',
        text: 'In maximal 3 Zügen auf der Fahrbahn wenden und zurückfahren.',
        subText: 'Links blinken • 3-S-Blick • Kein Bordsteinkontakt',
      }, false);
    } else if (this.currentMission.id === 'gefahrenbremsung') {
      this.setNavInstruction({
        id: 'gefahrenbremsung_start',
        icon: '⚡',
        title: 'GEFAHRENBREMSUNG',
        text: 'Auf mindestens 30 km/h beschleunigen und auf Signal warten.',
        subText: 'Sofortige Vollbremsung bei Aufforderung!',
      }, false);
    } else if (this.currentMission.id === 'kreisverkehr') {
      this.setNavInstruction({
        id: 'kreisverkehr_start',
        icon: '🔄',
        title: 'KREISVERKEHR',
        text: 'In den Kreisverkehr einfahren und die 2. Ausfahrt nehmen.',
        subText: 'Vorfahrt gewähren • Bei Einfahrt NICHT blinken',
      }, false);
    } else if (this.currentMission.id === 'sonderfahrt-autobahn') {
      this.setNavInstruction({
        id: 'ab_mission_start',
        icon: '⚡',
        title: 'SONDERFAHRT AUTOBAHN',
        text: 'Über Beschleunigungsstreifen einfädeln. Richtgeschwindigkeit 130 km/h.',
        spokenText: 'Sonderfahrt Autobahn. Beschleunigen Sie auf dem Beschleunigungsstreifen, fädeln Sie zügig ein und beachten Sie das Rechtsfahrgebot.',
        subText: 'Beschleunigen • Schulterblick • Blinker links • Rechtsfahrgebot',
      }, false);
    } else if (this.currentMission.id === 'sonderfahrt-ueberland') {
      this.setNavInstruction({
        id: 'land_mission_start',
        icon: '🧭',
        title: 'ÜBERLANDFAHRT',
        text: 'Ortsausgang passieren. Außerorts bis zu 100 km/h fahren.',
        spokenText: 'Sonderfahrt Überlandfahrt. Passieren Sie die Ortstafel und fahren Sie mit angepasster Geschwindigkeit bis 100 km/h.',
        subText: 'Ortstafel Z311 beachten • Vorausschauend fahren',
      }, false);
    } else if (this.currentMission.id === 'engstelle-parkende-autos') {
      this.setNavInstruction({
        id: 'engstelle_mission_start',
        icon: '🛑',
        title: 'ENGSTELLE',
        text: 'Vorbeifahren an parkenden Autos mit Blinker und Gegenverkehrsbeachtung.',
        spokenText: 'Engstellen-Training. Fahren Sie vorsichtig an den parkenden Fahrzeugen vorbei. Rechtzeitig links blinken und Gegenverkehr beachten.',
        subText: 'Blinker links • Gegenverkehr beachten • 1m Seitenabstand',
      }, false);
    } else {
      this.setNavInstruction({
        id: 'departure_prep',
        icon: '🧭',
        title: 'FAHRTBEGINN',
        text: 'Abfahrkontrolle durchführen und nach Norden anfahren.',
        spokenText: `Willkommen zur Fahrprüfung! Prüfungsaufgabe: ${this.currentMission.title}. Bitte führen Sie zuerst die Abfahrkontrolle im Cockpit durch: Sitz und Spiegel einstellen, anschnallen, Motor starten und Handbremse lösen.`,
        subText: 'Sitz • Spiegel • Gurt • Motor',
      }, false);
    }

    // Initial Examiner Welcome & Task Briefing with Pre-Drive checklist reminder
    this.examiner.say(
      `Willkommen zur Fahrprüfung! Prüfungsaufgabe: ${this.currentMission.title}. Bitte führen Sie zuerst die Abfahrkontrolle im Cockpit durch: Sitz und Spiegel einstellen, anschnallen, Motor starten und Handbremse lösen.`,
      false,
      true,
      'departure_prep'
    );
  }

  public update(deltaTime: number, vehiclePhysics: VehiclePhysics) {
    if (this.isCompleted) return;

    this.elapsedTime += deltaTime;
    const pos = new THREE.Vector3(
      vehiclePhysics.chassisBody.position.x,
      vehiclePhysics.chassisBody.position.y,
      vehiclePhysics.chassisBody.position.z
    );
    const speed = vehiclePhysics.speedKmh;

    // Mission-specific logic
    if (this.currentMission.id === 'laengsparken') {
      this.updateLaengsparken(vehiclePhysics, pos, speed);
    } else if (this.currentMission.id === 'querparken') {
      this.updateQuerparken(vehiclePhysics, pos, speed);
    } else if (this.currentMission.id === 'umkehren_wenden') {
      this.updateWenden(vehiclePhysics, pos, speed);
    } else if (this.currentMission.id === 'gefahrenbremsung') {
      this.updateGefahrenbremsung(deltaTime, vehiclePhysics, speed);
    } else if (this.currentMission.id === 'kreisverkehr') {
      this.updateKreisverkehr(deltaTime, pos, speed);
    } else if (
      this.currentMission.id === 'freies_fahren' ||
      this.currentMission.id === 'vollpruefung' ||
      this.currentMission.id === 'rechts_vor_links' ||
      this.currentMission.id === 'sonderfahrt-autobahn' ||
      this.currentMission.id === 'sonderfahrt-ueberland' ||
      this.currentMission.id === 'engstelle-parkende-autos'
    ) {
      this.updateFreeDriveInstructorGuidance(deltaTime, pos, speed, vehiclePhysics);
    }
  }

  // ── Intelligent Driving Instructor Navigation Guidance ────────────────────
  private navStepIndex: number = 0;
  private lastNavInstructionTime: number = 0;
  private hasDeparted: boolean = false;
  private navAnnouncedZones: Set<number> = new Set<number>();

  private say(text: string, audioKey?: string) {
    this.examiner.say(text, false, true, audioKey);
    this.lastNavInstructionTime = this.elapsedTime;
  }

  public getRelativeTurnDirection(forward: THREE.Vector3, targetDir: THREE.Vector3): 'links' | 'rechts' | 'geradeaus' {
    const crossY = forward.z * targetDir.x - forward.x * targetDir.z;
    if (crossY > 0.15) return 'links';
    if (crossY < -0.15) return 'rechts';
    return 'geradeaus';
  }

  private updateFreeDriveInstructorGuidance(
    _deltaTime: number,
    pos: THREE.Vector3,
    speed: number,
    physics: VehiclePhysics
  ) {
    const quat = new THREE.Quaternion(
      physics.chassisBody.quaternion.x,
      physics.chassisBody.quaternion.y,
      physics.chassisBody.quaternion.z,
      physics.chassisBody.quaternion.w
    );
    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(quat).normalize();

    // ── 0. Initial departure ─────────────────────────────────────────────────
    if (!this.hasDeparted && speed > 4.0) {
      this.hasDeparted = true;
      this.setNavInstruction({
        id: 'start_north',
        icon: '⬆️',
        title: 'HAUPTSTRASSE NORD',
        text: 'Geradeaus auf der Hauptstraße nach Norden fahren.',
        spokenText: 'Gut. Fahren Sie bitte geradeaus auf der Hauptstraße nach Norden weiter.',
        subText: 'Vorfahrtsstraße • Tempo 50',
      }, true);
      return;
    }
    if (!this.hasDeparted) return;

    // ── 0A. Autobahn Loop Sector (±550m to ±610m) ────────────────────────────
    if (Math.abs(pos.z) > 550 || Math.abs(pos.x) > 550) {
      this.setNavInstruction({
        id: 'ab_guidance',
        icon: '⚡',
        title: 'AUTOBAHN',
        text: 'Richtgeschwindigkeit 130 km/h. Rechtsfahrgebot beachten!',
        spokenText: 'Sie befinden sich auf der Autobahn. Richtgeschwindigkeit 130 km/h. Bitte halten Sie sich an das Rechtsfahrgebot.',
        subText: 'Tempo 130 • Rechtsfahrgebot • Kein Rechtsüberholen',
      }, true);
      return;
    }

    // ── 0B. Landstraße Sector (180m to 550m) ─────────────────────────────────
    if (Math.abs(pos.z) > 180 || Math.abs(pos.x) > 180) {
      this.setNavInstruction({
        id: 'land_guidance',
        icon: '🧭',
        title: 'LANDSTRASSE',
        text: 'Außerorts: Höchstgeschwindigkeit 100 km/h. Vorausschauend fahren.',
        spokenText: 'Sie fahren auf der Landstraße außerorts. Erlaubte Höchstgeschwindigkeit beträgt 100 km/h.',
        subText: 'Tempo 100 • Kurven anbremsen • Abstand halten',
      }, true);
      return;
    }

    // ── 1. North Boulevard (x ≈ 0, z >= 12) ──────────────────────────────────
    if (Math.abs(pos.x) < 7.0 && pos.z >= 12.0) {
      // 1A. Approaching North Ring T-Junction at (0, 0, 120) — directly facing guardrail! (48m advance notice)
      if (pos.z >= 72.0 && pos.z <= 126.0) {
        const dist = Math.max(0, Math.round(120 - pos.z));
        let subText = 'Vorfahrt gewähren (Zeichen 205) • Rechts blinken';
        let text = 'Vor der Leitplanke rechts abbiegen auf den Stadtring nach Osten.';
        if (dist <= 10) {
          subText = '⚡ JETZT RECHTS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Rechts abbiegen • Vorfahrt gewähren (Z. 205) • Rechts blinken';
        } else {
          text = `In ${dist} m an der T-Kreuzung rechts abbiegen auf den Stadtring.`;
          subText = `In ${dist} m vor Leitplanke rechts abbiegen • Vorfahrt gewähren (Z. 205)`;
        }
        this.setNavInstruction({
          id: 't_junction_north_ring',
          icon: '➡️',
          title: 'T-KREUZUNG STADTRING',
          text,
          spokenText: 'An der T-Kreuzung vor der Leitplanke bitte rechts abbiegen auf den Stadtring nach Osten.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // 1B. Boulevard North past traffic light heading towards North Ring
      if (pos.z >= 60.0 && pos.z < 72.0) {
        this.setNavInstruction({
          id: 'boulevard_north_past_tl',
          icon: '⬆️',
          title: 'HAUPTSTRASSE NORD',
          text: 'Dem Straßenverlauf nach Norden folgen bis zur T-Kreuzung am Stadtring.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, false);
        return;
      }

      // 1C. Approaching Signalized Intersection (0, 0, 60) from south (48m advance notice)
      if (pos.z >= 12.0 && pos.z < 60.0) {
        const dist = Math.max(0, Math.round(60 - pos.z));
        let subText = 'Lichtzeichenanlage beachten • Links blinken';
        let text = 'An der nächsten Ampel links abbiegen in die 30er-Zone.';
        if (dist <= 10) {
          subText = '⚡ JETZT LINKS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Links abbiegen • Links blinken & Gegenverkehr beachten';
        } else {
          text = `In ${dist} m an der Ampel links abbiegen in die 30er-Zone.`;
          subText = `In ${dist} m an Ampel links abbiegen • 3-S-Blick beachten`;
        }
        this.setNavInstruction({
          id: 'tl_junction_60_turn_left',
          icon: '⬅️',
          title: 'AMPELKREUZUNG',
          text,
          spokenText: 'An der nächsten Ampel links abbiegen in die 30er-Zone.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }
    }

    // ── 2. North Ring Road (z ≈ 120) ─────────────────────────────────────────
    if (Math.abs(pos.z - 120.0) < 8.0) {
      // 2A. Approaching NE corner at (120, 120) (65m advance notice)
      if (pos.x >= 55.0 && pos.x <= 126.0) {
        const dist = Math.max(0, Math.round(120 - pos.x));
        let subText = 'Kurve anbremsen • Tempo 50';
        let text = 'Rechts abbiegen auf den Ostring nach Süden.';
        if (dist <= 10) {
          subText = '⚡ JETZT RECHTS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Rechts abbiegen • Blinker rechts & Tempo auf 30 drosseln';
        } else {
          text = `In ${dist} m an der Ring-Ecke rechts abbiegen auf den Ostring nach Süden.`;
          subText = `In ${dist} m rechts abbiegen • Tempo 50`;
        }
        this.setNavInstruction({
          id: 'ring_ne_corner',
          icon: '➡️',
          title: 'RING-ECKE NORDOST',
          text,
          spokenText: 'In etwa 60 Metern an der Ring-Ecke bitte rechts abbiegen auf den Ostring nach Süden.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // 2B. Heading East on North Ring
      if (pos.x > 7.0 && pos.x < 55.0) {
        this.setNavInstruction({
          id: 'north_ring_east',
          icon: '⬆️',
          title: 'STADTRING NORD',
          text: 'Dem Stadtring nach Osten folgen bis zur Ringecke.',
          spokenText: 'Sehr gut! Folgen Sie dem Stadtring nach Osten bis zur Ringecke.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, true);
        return;
      }

      // 2C. Approaching NW corner at (-120, 120) (65m advance notice)
      if (pos.x <= -55.0 && pos.x >= -126.0) {
        const dist = Math.max(0, Math.round(pos.x - (-120)));
        let subText = 'Kurve anbremsen • Tempo 50';
        let text = 'Links abbiegen auf den Westring nach Süden.';
        if (dist <= 10) {
          subText = '⚡ JETZT LINKS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Links abbiegen • Blinker links & Tempo auf 30 drosseln';
        } else {
          text = `In ${dist} m an der Ring-Ecke links abbiegen auf den Westring nach Süden.`;
          subText = `In ${dist} m links abbiegen • Tempo 50`;
        }
        this.setNavInstruction({
          id: 'ring_nw_corner',
          icon: '⬅️',
          title: 'RING-ECKE NORDWEST',
          text,
          spokenText: 'In etwa 60 Metern an der Ring-Ecke bitte links abbiegen auf den Westring nach Süden.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // 2D. Heading West on North Ring
      if (pos.x < -7.0 && pos.x > -55.0) {
        this.setNavInstruction({
          id: 'north_ring_west',
          icon: '⬆️',
          title: 'STADTRING NORD',
          text: 'Dem Stadtring nach Westen folgen bis zur Ringecke.',
          spokenText: 'Sehr gut! Folgen Sie dem Stadtring nach Westen bis zur Ringecke.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, true);
        return;
      }
    }

    // ── 3. East Ring Road (x ≈ 120) ──────────────────────────────────────────
    if (Math.abs(pos.x - 120.0) < 8.0) {
      // 3A. Approaching SE corner at (120, -120) (65m advance notice)
      if (pos.z <= -55.0 && pos.z >= -126.0) {
        const dist = Math.max(0, Math.round(pos.z - (-120)));
        let subText = 'Kurve anbremsen • Tempo 50';
        let text = 'Rechts abbiegen auf den Südring nach Westen.';
        if (dist <= 10) {
          subText = '⚡ JETZT RECHTS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Rechts abbiegen • Blinker rechts & Tempo auf 30 drosseln';
        } else {
          text = `In ${dist} m an der Ring-Ecke rechts abbiegen auf den Südring nach Westen.`;
          subText = `In ${dist} m rechts abbiegen • Tempo 50`;
        }
        this.setNavInstruction({
          id: 'ring_se_corner',
          icon: '➡️',
          title: 'RING-ECKE SÜDOST',
          text,
          spokenText: 'In etwa 60 Metern an der Ring-Ecke bitte rechts abbiegen auf den Südring nach Westen.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // 3B. Driving south along East Ring
      if (pos.z > -55.0 && pos.z < 95.0) {
        this.setNavInstruction({
          id: 'east_ring_south',
          icon: '⬆️',
          title: 'OSTRING',
          text: 'Dem Ostring nach Süden folgen.',
          spokenText: 'Gut. Folgen Sie dem Außenring Richtung Süden. Achten Sie auf die Geschwindigkeit — Tempo 50 gilt auch hier.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, true);
        return;
      }
    }

    // ── 4. South Ring Road (z ≈ -120) ────────────────────────────────────────
    if (Math.abs(pos.z - (-120.0)) < 8.0) {
      // 4A. Approaching Main Boulevard South at x ≈ 0 (48m advance notice)
      if (Math.abs(pos.x) < 48.0) {
        const dist = Math.max(0, Math.round(Math.abs(pos.x)));
        const isWestbound = forward.x < 0;
        const icon = isWestbound ? '➡️' : '⬅️';
        const turnName = isWestbound ? 'rechts' : 'links';
        let subText = `${turnName === 'rechts' ? 'Rechts' : 'Links'} blinken • Schulterblick`;
        let text = `${turnName === 'rechts' ? 'Rechts' : 'Links'} abbiegen auf die Hauptstraße nach Norden.`;
        if (dist <= 10) {
          subText = `⚡ JETZT ${turnName.toUpperCase()} ABBIEGEN!`;
        } else if (dist <= 25) {
          subText = `${turnName.toUpperCase()} abbiegen • Blinker & Schulterblick`;
        } else {
          text = `In ${dist} m ${turnName} abbiegen auf die Hauptstraße nach Norden.`;
          subText = `In ${dist} m ${turnName} abbiegen • Zurück zum Start`;
        }
        this.setNavInstruction({
          id: 'south_ring_turn_north',
          icon,
          title: 'HAUPTSTRASSE SÜD',
          text,
          spokenText: `In etwa 50 Metern bitte ${turnName} abbiegen auf die Hauptstraße nach Norden und geradeaus zurück zum Startpunkt.`,
          audioKey: `south_ring_turn_north_${turnName}`,
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // 4B. Cruising along South Ring
      if (Math.abs(pos.x) >= 48.0 && Math.abs(pos.x) < 110.0) {
        this.setNavInstruction({
          id: 'south_ring_cruise',
          icon: '⬆️',
          title: 'SÜDRING',
          text: 'Dem Südring folgen bis zur Hauptstraße.',
          spokenText: 'Folgen Sie dem Südring bis zur Hauptstraße und biegen Sie dann ab zurück zum Ausgangsort.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, true);
        return;
      }
    }

    // ── 5. West Ring Road (x ≈ -120) ─────────────────────────────────────────
    if (Math.abs(pos.x - (-120.0)) < 8.0) {
      // 5A. Approaching SW corner at (-120, -120) (65m advance notice)
      if (pos.z <= -55.0 && pos.z >= -126.0) {
        const dist = Math.max(0, Math.round(pos.z - (-120)));
        let subText = 'Kurve anbremsen • Tempo 50';
        let text = 'Links abbiegen auf den Südring nach Osten.';
        if (dist <= 10) {
          subText = '⚡ JETZT LINKS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Links abbiegen • Blinker links & Tempo auf 30 drosseln';
        } else {
          text = `In ${dist} m an der Ring-Ecke links abbiegen auf den Südring nach Osten.`;
          subText = `In ${dist} m links abbiegen • Tempo 50`;
        }
        this.setNavInstruction({
          id: 'ring_sw_corner',
          icon: '⬅️',
          title: 'RING-ECKE SÜDWEST',
          text,
          spokenText: 'In etwa 60 Metern an der Ring-Ecke bitte links abbiegen auf den Südring nach Osten.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // 5B. Driving south along West Ring
      if (pos.z > -55.0 && pos.z < 95.0) {
        this.setNavInstruction({
          id: 'west_ring_south',
          icon: '⬆️',
          title: 'WESTRING',
          text: 'Dem Westring nach Süden folgen.',
          spokenText: 'Dem Außenring nach Süden folgen. Tempo 50 beachten.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, true);
        return;
      }
    }

    // ── 6. South Boulevard (x ≈ 0, z <= -18) ─────────────────────────────────
    if (Math.abs(pos.x) < 7.0 && pos.z <= -18.0) {
      if (forward.z > 0 && pos.z > -65.0 && pos.z < -18.0) {
        const dist = Math.max(0, Math.round(Math.abs(pos.z) - 18));
        let subText = 'Vorfahrt gewähren • Bei Einfahrt nicht blinken';
        let text = 'In den Kreisverkehr einfahren und die 2. Ausfahrt (geradeaus) nehmen.';
        if (dist <= 10) {
          subText = '⚡ IN KREISVERKEHR EINFAHREN!';
        } else if (dist <= 25) {
          subText = 'Kreisverkehr: Vorfahrt gewähren (Z. 215) • Bei Einfahrt NICHT blinken';
        } else {
          text = `In ${dist} m in den Kreisverkehr einfahren (2. Ausfahrt geradeaus).`;
          subText = `In ${dist} m Kreisverkehr • Vorfahrt gewähren (Z. 215)`;
        }
        this.setNavInstruction({
          id: 'roundabout_from_south',
          icon: '🔄',
          title: 'KREISVERKEHR',
          text,
          spokenText: 'In etwa 50 Metern in den Kreisverkehr einfahren, zweite Ausfahrt geradeaus nehmen.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      if (this.currentMission.id === 'vollpruefung' && pos.z > -70.0) {
        this.completeMission(true, undefined, 'Fahrprüfung erfolgreich bestanden! Rundstrecke fehlerfrei absolviert.');
        return;
      }

      this.setNavInstruction({
        id: 'boulevard_south_heading_north',
        icon: '⬆️',
        title: 'HAUPTSTRASSE SÜD',
        text: 'Der Hauptstraße nach Norden folgen Richtung Startpunkt.',
        subText: 'Vorfahrtsstraße • Tempo 50',
      }, true);
      return;
    }

    // ── 7. Residential 30-Zone North Street (z ≈ 60) ─────────────────────────
    if (Math.abs(pos.z - 60.0) < 8.0) {
      // 7A. West branch (x < -7)
      if (pos.x < -7.0) {
        // Approaching West Cross Street at x = -50 (26m advance notice in 30-zone)
        if (pos.x <= -24.0 && pos.x >= -58.0) {
          const dist = Math.max(0, Math.round(Math.abs(pos.x - (-50))));
          const targetDir = new THREE.Vector3(0, 0, -1);
          const turnDir = this.getRelativeTurnDirection(forward, targetDir);
          let subText = '§ 8 StVO Rechts-vor-Links • Blinker setzen';
          let text = `An der Kreuzung ${turnDir} abbiegen in die Querstraße nach Süden.`;
          if (dist <= 8) {
            subText = `⚡ JETZT ${turnDir.toUpperCase()} ABBIEGEN!`;
          } else if (dist <= 16) {
            subText = `Rechts-vor-Links beachten! ${turnDir.toUpperCase()} blinken`;
          } else {
            text = `In ${dist} m an der Kreuzung ${turnDir} abbiegen nach Süden.`;
            subText = `In ${dist} m Kreuzung • Rechts-vor-Links (§ 8 StVO)`;
          }
          this.setNavInstruction({
            id: 'res_cross_w_turn_south',
            icon: turnDir === 'links' ? '⬅️' : '➡️',
            title: 'RECHTS-VOR-LINKS',
            text,
            spokenText: `An der nächsten Kreuzung gilt Rechts-vor-Links! Bitte ${turnDir} abbiegen und dann der Querstraße nach Süden folgen.`,
            audioKey: `res_cross_w_turn_south_${turnDir}`,
            subText,
            distanceMeters: dist,
          }, true);
          return;
        }

        // Inside 30-zone heading West
        if (pos.x > -24.0 && pos.x < -7.0) {
          this.setNavInstruction({
            id: 'res_30_west',
            icon: '⬆️',
            title: 'TEMPO-30-ZONE',
            text: 'Geradeaus fahren bis zur nächsten Querstraße.',
            spokenText: 'Sehr gut! Sie sind jetzt in der Tempo-30-Zone. An allen unbeschilderten Kreuzungen gilt Rechts-vor-Links. Fahren Sie bitte geradeaus bis zur nächsten Querstraße.',
            subText: 'Tempo 30 • Rechts-vor-Links beachten',
          }, true);
          return;
        }
      }

      // 7B. East branch (x > 7)
      if (pos.x > 7.0) {
        // Approaching East Cross Street at x = +50 (26m advance notice in 30-zone)
        if (pos.x >= 24.0 && pos.x <= 58.0) {
          const dist = Math.max(0, Math.round(Math.abs(50 - pos.x)));
          const targetDir = new THREE.Vector3(0, 0, -1);
          const turnDir = this.getRelativeTurnDirection(forward, targetDir);
          let subText = '§ 8 StVO Rechts-vor-Links • Blinker setzen';
          let text = `An der Kreuzung ${turnDir} abbiegen in die Querstraße nach Süden.`;
          if (dist <= 8) {
            subText = `⚡ JETZT ${turnDir.toUpperCase()} ABBIEGEN!`;
          } else if (dist <= 16) {
            subText = `Rechts-vor-Links beachten! ${turnDir.toUpperCase()} blinken`;
          } else {
            text = `In ${dist} m an der Kreuzung ${turnDir} abbiegen nach Süden.`;
            subText = `In ${dist} m Kreuzung • Rechts-vor-Links (§ 8 StVO)`;
          }
          this.setNavInstruction({
            id: 'res_cross_e_turn_south',
            icon: turnDir === 'links' ? '⬅️' : '➡️',
            title: 'RECHTS-VOR-LINKS',
            text,
            spokenText: `An der nächsten Kreuzung gilt Rechts-vor-Links! Bitte ${turnDir} abbiegen und dann der Querstraße nach Süden folgen.`,
            audioKey: `res_cross_e_turn_south_${turnDir}`,
            subText,
            distanceMeters: dist,
          }, true);
          return;
        }

        // Inside 30-zone heading East
        if (pos.x < 24.0 && pos.x > 7.0) {
          this.setNavInstruction({
            id: 'res_30_east',
            icon: '⬆️',
            title: 'TEMPO-30-ZONE',
            text: 'Geradeaus fahren bis zur nächsten Querstraße.',
            spokenText: 'Sehr gut! Sie sind jetzt in der Tempo-30-Zone. An allen unbeschilderten Kreuzungen gilt Rechts-vor-Links. Fahren Sie bitte geradeaus bis zur nächsten Querstraße.',
            subText: 'Tempo 30 • Rechts-vor-Links beachten',
          }, true);
          return;
        }
      }
    }

    // ── 8. Residential Cross Streets (x ≈ -50 or x ≈ 50) ──────────────────────
    if (Math.abs(Math.abs(pos.x) - 50.0) < 8.0) {
      if (pos.z < 54.0 && pos.z > 32.0) {
        if (this.currentMission.id === 'rechts_vor_links') {
          this.completeMission(true, 95, 'Rechts-vor-Links im Wohngebiet erfolgreich gemeistert!');
          return;
        }
        const nextTurn = pos.x > 0 ? 'links' : 'rechts';
        this.setNavInstruction({
          id: 'cross_street_south',
          icon: '⬆️',
          title: 'QUERSTRASSE',
          text: 'Der Querstraße nach Süden folgen bis zur Hauptstraße.',
          spokenText: `Sehr gut! Fahren Sie bitte die Querstraße entlang nach Süden. An der Kreuzung mit der Hauptstraße biegen wir dann ${nextTurn} ab Richtung Kreisverkehr.`,
          subText: 'Tempo 30 • Bremsbereit bleiben',
        }, true);
        return;
      }

      if (pos.z <= 32.0 && pos.z >= 0.0) {
        const dist = Math.max(0, Math.round(pos.z));
        const turn = pos.x > 0 ? 'links' : 'rechts';
        let subText = `Vorfahrt gewähren • ${turn === 'links' ? 'Links' : 'Rechts'} blinken`;
        let text = `${turn === 'links' ? 'Links' : 'Rechts'} abbiegen auf die Hauptstraße Richtung Kreisverkehr.`;
        if (dist <= 8) {
          subText = `⚡ JETZT ${turn.toUpperCase()} ABBIEGEN!`;
        } else if (dist <= 16) {
          subText = `Vorfahrt gewähren • ${turn.toUpperCase()} blinken & Schulterblick`;
        } else {
          text = `In ${dist} m ${turn} abbiegen auf die Vorfahrtsstraße.`;
          subText = `In ${dist} m Vorfahrtsstraße • Vorfahrt gewähren`;
        }
        this.setNavInstruction({
          id: 'cross_street_approach_main_e_w',
          icon: turn === 'links' ? '⬅️' : '➡️',
          title: 'VORFAHRTSSTRASSE',
          text,
          spokenText: `Jetzt ${turn} abbiegen auf die Vorfahrtsstraße Richtung Kreisverkehr. Denken Sie an den Schulterblick und ${turn} blinken!`,
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }
    }

    // ── 9. Main Avenue East-West (z ≈ 0) & Roundabout ───────────────────────
    if (Math.abs(pos.z) < 8.0) {
      // Approaching Roundabout from West (47m advance notice)
      if (pos.x > -65.0 && pos.x < -18.0) {
        const dist = Math.max(0, Math.round(Math.abs(pos.x) - 18));
        let subText = 'Vorfahrt gewähren • Bei Einfahrt nicht blinken';
        let text = 'In den Kreisverkehr einfahren und die 2. Ausfahrt (geradeaus) nehmen.';
        if (dist <= 10) {
          subText = '⚡ IN KREISVERKEHR EINFAHREN!';
        } else if (dist <= 25) {
          subText = 'Kreisverkehr: Vorfahrt gewähren • Bei Einfahrt NICHT blinken';
        } else {
          text = `In ${dist} m in den Kreisverkehr einfahren (2. Ausfahrt geradeaus).`;
          subText = `In ${dist} m Kreisverkehr • Vorfahrt gewähren`;
        }
        this.setNavInstruction({
          id: 'approach_roundabout_from_w',
          icon: '🔄',
          title: 'KREISVERKEHR',
          text,
          spokenText: 'In den Kreisverkehr einfahren, zweite Ausfahrt geradeaus nehmen. Bei der Einfahrt nicht blinken.',
          subText,
          distanceMeters: dist,
        }, true);
        return;
      }

      // Heading East from Roundabout
      if (pos.x > 18.0 && pos.x < 65.0) {
        this.setNavInstruction({
          id: 'east_avenue_to_ring',
          icon: '⬆️',
          title: 'HAUPTSTRASSE OST',
          text: 'Der Vorfahrtsstraße nach Osten folgen bis zum Stadtring.',
          spokenText: 'Der Vorfahrtsstraße nach Osten folgen bis zum Stadtring.',
          subText: 'Vorfahrtsstraße • Tempo 50',
        }, true);
        return;
      }

      // Approaching East Ring T-Junction (55m advance notice)
      if (pos.x >= 65.0 && pos.x <= 126.0) {
        const dist = Math.max(0, Math.round(120 - pos.x));
        let subText = 'Vorfahrt gewähren • Rechts blinken';
        let text = 'Rechts abbiegen auf den Stadtring nach Süden.';
        if (dist <= 10) {
          subText = '⚡ JETZT RECHTS ABBIEGEN!';
        } else if (dist <= 25) {
          subText = 'Rechts abbiegen • Vorfahrt gewähren & Rechts blinken';
        } else {
          text = `In ${dist} m rechts abbiegen auf den Stadtring nach Süden.`;
          subText = `In ${dist} m T-Kreuzung • Vorfahrt gewähren`;
        }
        this.setNavInstruction({
          id: 'east_ring_t_junction',
          icon: '➡️',
          title: 'T-KREUZUNG OSTRING',
          text,
          spokenText: 'An der T-Kreuzung bitte rechts abbiegen auf den Stadtring nach Süden.',
          subText,
          distanceMeters: dist,
        }, true);
      }
    }

    // ── 10. Inside Roundabout ────────────────────────────────────────────────
    if (Math.hypot(pos.x, pos.z) <= 18.5) {
      this.setNavInstruction({
        id: 'inside_roundabout',
        icon: '🔄',
        title: 'KREISVERKEHR',
        text: 'Kreisverkehr an der nächsten Ausfahrt verlassen (blinken!).',
        spokenText: 'Jetzt rechten Blinker setzen, Schulterblick machen und an der nächsten Ausfahrt ausfahren.',
        subText: 'Blinker rechts setzen vor Ausfahrt',
      }, true);
      return;
    }

    // ── 11. Fallback Guidance ────────────────────────────────────────────────
    if (!this.currentNavInstruction) {
      this.setNavInstruction({
        id: 'default_cruise',
        icon: '⬆️',
        title: 'FAHRTWEG',
        text: 'Dem Straßenverlauf folgen und StVO beachten.',
        subText: 'Tempo & Vorfahrt beachten',
      }, false);
    }
  }

  private updateLaengsparken(vehiclePhysics: VehiclePhysics, pos: THREE.Vector3, speed: number) {
    if (!this.currentNavInstruction || this.activeInstructionId !== 'laengsparken_active') {
      this.setNavInstruction({
        id: 'laengsparken_active',
        icon: '🅿️',
        title: 'LÄNGSPARKEN',
        text: 'Rückwärts in die markierte Parklücke einparken.',
        subText: 'Blinker rechts • 3-S-Blick • Kein Bordsteinkontakt',
      }, false);
    }

    const quat = new THREE.Quaternion(
      vehiclePhysics.chassisBody.quaternion.x,
      vehiclePhysics.chassisBody.quaternion.y,
      vehiclePhysics.chassisBody.quaternion.z,
      vehiclePhysics.chassisBody.quaternion.w
    );

    const evalResult = this.town.parkingZones.evaluateParallelParking(pos, quat, speed);

    if (evalResult.curbMounted) {
      this.examiner.say('🛑 Auf den Bordstein aufgefahren! Die Aufgabe ist damit nicht bestanden.', true);
      this.completeMission(false, 0, 'Bordstein gerammt');
    } else if (evalResult.isParked && evalResult.passed) {
      this.examiner.say(`Hervorragend! ${evalResult.feedback}`);
      this.completeMission(true, evalResult.score, evalResult.feedback);
    }
  }

  private updateQuerparken(vehiclePhysics: VehiclePhysics, pos: THREE.Vector3, speed: number) {
    if (!this.currentNavInstruction || this.activeInstructionId !== 'querparken_active') {
      this.setNavInstruction({
        id: 'querparken_active',
        icon: '🅿️',
        title: 'QUERPARKEN',
        text: 'Rückwärts mittig in die grün markierte 90°-Parkbucht einparken.',
        subText: 'Blinker rechts • 3-S-Blick • Stillstand in Parkbucht',
      }, false);
    }

    const quat = new THREE.Quaternion(
      vehiclePhysics.chassisBody.quaternion.x,
      vehiclePhysics.chassisBody.quaternion.y,
      vehiclePhysics.chassisBody.quaternion.z,
      vehiclePhysics.chassisBody.quaternion.w
    );

    const evalResult = this.town.parkingZones.evaluateBayParking(pos, quat, speed);
    if (evalResult.isParked && evalResult.passed) {
      this.examiner.say(`Sehr gut! ${evalResult.feedback}`);
      this.completeMission(true, evalResult.score, evalResult.feedback);
    }
  }

  private updateWenden(vehiclePhysics: VehiclePhysics, pos: THREE.Vector3, speed: number) {
    if (!this.currentNavInstruction || this.activeInstructionId !== 'wenden_active') {
      this.setNavInstruction({
        id: 'wenden_active',
        icon: '🔄',
        title: 'UMKEHREN / WENDEN',
        text: 'Auf der engen Fahrbahn in max. 3 Zügen wenden.',
        subText: 'Links blinken • Rückraum sichern • Kein Bordsteinkontakt',
      }, false);
    }

    const ptState = vehiclePhysics.powertrain.getState(speed);
    const isReversing = ptState.effectiveGear === -1 || speed < -0.5;
    if (isReversing && !this.wendenWasReversing) {
      this.wendenReverseCount++;
      this.wendenWasReversing = true;
    } else if (!isReversing && speed > 1.0) {
      this.wendenWasReversing = false;
    }

    const quat = new THREE.Quaternion(
      vehiclePhysics.chassisBody.quaternion.x,
      vehiclePhysics.chassisBody.quaternion.y,
      vehiclePhysics.chassisBody.quaternion.z,
      vehiclePhysics.chassisBody.quaternion.w
    );

    const evalResult = this.town.parkingZones.evaluateThreePointTurn(
      pos,
      quat,
      speed,
      this.wendenInitialForwardZ,
      this.wendenReverseCount
    );

    if (evalResult.curbHit) {
      this.examiner.say('🛑 Beim Wenden auf den Bordstein aufgefahren! Aufgabe nicht bestanden.', true);
      this.completeMission(false, 0, evalResult.feedback);
    } else if (evalResult.completed && evalResult.passed) {
      this.examiner.say(`Ausgezeichnet! ${evalResult.feedback}`);
      this.completeMission(true, evalResult.score, evalResult.feedback);
    }
  }

  private updateGefahrenbremsung(deltaTime: number, vehiclePhysics: VehiclePhysics, speed: number) {
    if (!this.emergencyBrakingTriggered) {
      if (!this.currentNavInstruction || this.activeInstructionId !== 'gefahrenbremsung_accel') {
        this.setNavInstruction({
          id: 'gefahrenbremsung_accel',
          icon: '⚡',
          title: 'GEFAHRENBREMSUNG',
          text: 'Auf mindestens 30 km/h beschleunigen und auf Prüferkommando warten.',
          subText: 'Sofortige Vollbremsung bei Aufforderung!',
        }, false);
      }
    }

    if (!this.emergencyBrakingTriggered && speed >= 28) {
      this.emergencyBrakingTriggered = true;
      this.emergencyBrakingReactionStart = this.elapsedTime;
      this.emergencyBrakingBrakeTime = 0;
      this.setNavInstruction({
        id: 'gefahrenbremsung_stop',
        icon: '🛑',
        title: 'GEFAHRENBREMSUNG!',
        text: 'VOLLBREMSUNG! Jetzt sofort voll in die Eisen treten!',
        spokenText: 'GEFAHRENBREMSUNG! JETZT VOLLBREMSUNG!',
        subText: 'Kupplung und Bremse voll durchtreten!',
      }, true);
      this.examiner.say('GEFAHRENBREMSUNG! JETZT VOLLBREMSUNG!', true);
    } else if (this.emergencyBrakingTriggered && !this.emergencyBrakingCompleted) {
      // Record driver reaction timestamp when brake pedal is depressed
      if (this.emergencyBrakingBrakeTime === 0 && vehiclePhysics.smoothedBrakePressure > 0.2) {
        this.emergencyBrakingBrakeTime = this.elapsedTime;
      }

      if (Math.abs(speed) < 0.3) {
        this.emergencyBrakingCompleted = true;
        const reactionTime = (this.emergencyBrakingBrakeTime > 0 ? this.emergencyBrakingBrakeTime : this.elapsedTime) - this.emergencyBrakingReactionStart;
        const totalStopTime = this.elapsedTime - this.emergencyBrakingReactionStart;
        const roundedReaction = Math.round(reactionTime * 100) / 100;
        const roundedTotal = Math.round(totalStopTime * 10) / 10;

        if (reactionTime <= 0.85 && totalStopTime <= 3.5) {
          const score = Math.max(75, Math.min(100, Math.round(100 - (reactionTime - 0.3) * 40)));
          this.examiner.say(`Sehr gute Reaktionszeit (${roundedReaction} s) und saubere Vollbremsung in ${roundedTotal} s!`);
          this.completeMission(true, score, `Gefahrenbremsung erfolgreich! Reaktionszeit: ${roundedReaction}s, Bremszeit: ${roundedTotal}s`);
        } else {
          this.examiner.say(`Zu späte Reaktion (${roundedReaction} s) oder zu lange Bremszeit (${roundedTotal} s)! In der Prüfung nicht ausreichend.`, true);
          this.completeMission(false, 40, `Reaktionszeit (${roundedReaction}s) oder Bremsweg zu lang`);
        }
      }
    }
  }

  private updateKreisverkehr(deltaTime: number, pos: THREE.Vector3, speed: number) {
    const dist = Math.hypot(pos.x - this.town.roundabout.center.x, pos.z - this.town.roundabout.center.z);
    const isInside = this.town.roundabout.isInsideRoundabout(pos);

    if (isInside || dist <= 20.5) {
      this.roundaboutEntered = true;
      if (isInside) {
        this.roundaboutCirculateTime += deltaTime;
        this.setNavInstruction({
          id: 'kreisverkehr_inside_m',
          icon: '🔄',
          title: 'KREISVERKEHR',
          text: 'Rechten Blinker setzen und 2. Ausfahrt ausfahren.',
          spokenText: 'Jetzt rechten Blinker setzen, Schulterblick machen und an der 2. Ausfahrt ausfahren.',
          subText: 'Blinker rechts • Schulterblick vor Ausfahrt',
        }, true);
      }
    } else {
      this.setNavInstruction({
        id: 'kreisverkehr_approach_m',
        icon: '🔄',
        title: 'KREISVERKEHR',
        text: 'In den Kreisverkehr einfahren und die 2. Ausfahrt nehmen.',
        subText: 'Vorfahrt gewähren • Bei Einfahrt NICHT blinken',
        distanceMeters: Math.max(0, Math.round(dist - 20.5)),
      }, false);
    }

    if (this.roundaboutEntered && this.roundaboutCirculateTime > 1.5 && dist > 21.0 && speed > 4) {
      this.completeMission(true, 100, 'Kreisverkehr vorschriftsmäßig nach StVO gemeistert!');
    }
  }

  public completeMission(passed: boolean, customScore?: number, feedback?: string) {
    if (this.isCompleted) return;
    this.isCompleted = true;

    const infractions = this.examiner.infractionLog.infractions;
    const hasFatal = this.examiner.infractionLog.hasFatalInfraction;
    const finalPassed = passed && !hasFatal;
    const finalScore = customScore !== undefined ? customScore : this.examiner.infractionLog.totalScore;

    this.result = {
      missionId: this.currentMission.id,
      missionTitle: this.currentMission.title,
      passed: finalPassed,
      score: finalScore,
      durationSeconds: Math.round(this.elapsedTime),
      infractionsCount: infractions.length,
      fatalCount: infractions.filter((i) => i.severity === 'FATAL').length,
      feedback: feedback || (finalPassed ? 'Prüfungsaufgabe erfolgreich bestanden!' : 'Leider nicht bestanden.'),
    };

    const finalSpeech = finalPassed
      ? `Herzlichen Glückwunsch! Sie haben die Aufgabe ${this.currentMission.title} bestanden!`
      : `Die Aufgabe ${this.currentMission.title} wurde leider nicht bestanden. Bitte analysieren Sie Ihre Fahrfehler im Replay.`;

    this.examiner.say(finalSpeech, !finalPassed);
  }
}
